import { cloneDeep } from 'lodash';
import type { SafeLuminary, TemplateWatermarkSource } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

export type SourceTemplateStyling = {
    fonts?: string[];
    watermarkSources?: TemplateWatermarkSource[];
    watermark?: Record<string, unknown>;
};

/**
 * Pads the source video's fonts up to the target template's font slot count
 * by repeating the last source font. Without this, assigning a shorter source
 * fonts array directly to `template.fonts` truncates slots that the target
 * template's scene designs reference (e.g. `themeRef_fonts_2` against a
 * 2-font array), causing Luminary's validateTemplateReference to throw.
 */
export function padSourceFontsToTargetSlots(
    sourceFonts: string[],
    targetSlotCount: number,
): string[] {
    if (sourceFonts.length === 0) {
        return [];
    }
    const slots = Math.max(targetSlotCount, sourceFonts.length);
    const lastFont = sourceFonts[sourceFonts.length - 1];
    return Array.from({ length: slots }, (_, i) => sourceFonts[i] ?? lastFont);
}

export function applySourceTemplateStylingToLuminary(
    luminary: SafeLuminary,
    sourceTemplateStyling: SourceTemplateStyling,
) {
    luminary.batchRun((safeLuminary) => {
        if (sourceTemplateStyling.fonts && sourceTemplateStyling.fonts.length > 0) {
            safeLuminary.template.fonts = padSourceFontsToTargetSlots(
                sourceTemplateStyling.fonts,
                safeLuminary.template.fonts?.length ?? 0,
            );
        }
        if (
            sourceTemplateStyling.watermarkSources &&
            sourceTemplateStyling.watermarkSources.length > 0
        ) {
            safeLuminary.template.replaceWatermarkSources(
                cloneDeep(sourceTemplateStyling.watermarkSources),
            );
            safeLuminary.template.watermark.sourceData =
                VideoData.templateWatermarkSourcesByIndex(0);
            safeLuminary.template.watermark.visible = true;
        }
        if (sourceTemplateStyling.watermark) {
            safeLuminary.template.watermark.load(sourceTemplateStyling.watermark);
        }
    });
}
